import React, { useEffect, useState, useCallback, useMemo } from 'react';

import { ICusNewSearchProps } from './cus-new-search.props.autogenerated';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import classnames from 'classnames';
import { format } from '@msdyn365-commerce/retail-proxy';
import { useScrollLock } from '../../shared/hooks/useScrollLock';
import { useDebounce } from '../../shared/hooks/useDebounce';
import { useSearchSuggestions } from '../../integrations/relewise/hooks/useSearchSuggestions';
import { MappedProduct, SearchSuggestion } from '../../integrations/relewise/types/mapped';
import { ImageWithLoading } from '../../shared/components/image-with-loading/image-with-loading';
import { IGridSettings, IViewPort } from '@msdyn365-commerce/core-internal';
import { BaseImageSettings, useImageSettings } from '../../shared/hooks/useImageSettings';
import { Skeleton } from '../../shared/components/skeleton/skeleton';
import { ColorPalettePreview } from '../../shared/components/color-palette-preview/color-palette-preview';
import { ProductLabels } from '../../shared/components/product-labels/product-labels';
import { MemberPriceTag } from '../../shared/components/member-price-tag/member-price-tag';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { SEARCH_MODULE_NAME } from './cus-new-search.const';
import { useGenderCookieValue } from '../../shared/services/gender-cookie-service/hooks/use-gender-cookie-value';
import type { Gender } from '../../shared/services/gender-cookie-service/gender-cookie-service.types';
import Button from '../../components/custom/button/button';
import { DEFAULT_GENDER, MIN_GENDERS_FOR_SHOWING_FILTERS } from '../../shared/services/gender-cookie-service/gender-cookie-service.const';
import { getAvailableGendersForLocale } from '../../shared/services/gender-cookie-service/gender-cookie-service';

const FALLBACK_MAX_SEARCH_CHARS = 50;
const DEBOUNCE_DELAY_MS = 400;
const IMAGE_VIEWPORT_SETTINGS: BaseImageSettings = {
    xs: 'small',
    sm: 'small',
    md: 'small',
    lg: 'small',
    xl: 'small'
};
const MAX_NUMBER_OF_CATEGORY_SUGGESTIONS = 10;
const DEFAULT_NUMBER_OF_CATEGORY_SUGGESTIONS = 4;
const MAX_NUMBER_OF_PRODUCT_SUGGESTIONS = 30;
const DEFAULT_NUMBER_OF_PRODUCT_SUGGESTIONS = 10;
const MAX_NUMBER_OF_KEYWORD_SUGGESTIONS = 10;
const DEFAULT_NUMBER_OF_KEYWORD_SUGGESTIONS = 3;

interface SimpleSuggestionListProps {
    items: SearchSuggestion[] | undefined;
    numberOfSuggestions: number;
    isLoading: boolean;
    emptyText: string;
}
const SimpleSuggestionList = ({ items, isLoading, numberOfSuggestions, emptyText }: SimpleSuggestionListProps) => {
    if (isLoading) {
        return (
            <ul className='simple-suggestion-list'>
                {Array.from({ length: numberOfSuggestions }).map((_, index) => (
                    <li key={index}>
                        <Skeleton className='simple-suggestion-list__item simple-suggestion-list__item-skeleton' />
                    </li>
                ))}
            </ul>
        );
    }
    if (!isLoading && !items?.length) {
        return <div className='simple-suggestion-list simple-suggestion-list--empty'>{emptyText}</div>;
    }
    return (
        <ul className='simple-suggestion-list'>
            {items?.map(item => (
                <li key={item.id}>
                    <a className='simple-suggestion-list__item' href={item.url}>
                        {item.label}
                    </a>
                </li>
            ))}
        </ul>
    );
};

interface ProductSuggestionListProps {
    products: MappedProduct[] | undefined;
    imageSettings: IViewPort;
    gridSettings: IGridSettings | undefined;
    numberOfSuggestions: number;
    isLoading: boolean;
    emptyText: string;
    maxProductLabels: number | undefined;
    telemetry: ITelemetry;
}
const ProductSuggestionList = ({
    products,
    imageSettings,
    gridSettings,
    isLoading,
    numberOfSuggestions,
    emptyText,
    maxProductLabels,
    telemetry
}: ProductSuggestionListProps) => {
    if (isLoading) {
        return (
            <ul className='product-suggestion-list'>
                {Array.from({ length: numberOfSuggestions }).map((_, index) => (
                    <li key={index} className='product-suggestion-list__item-wrapper'>
                        <div className='product-suggestion-list__item'>
                            <Skeleton className='product-suggestion-list__image-wrapper' />
                            <div className='product-suggestion-list__text-container'>
                                <Skeleton className='product-suggestion-list__title product-suggestion-list__title-skeleton' />
                                <Skeleton className='product-suggestion-list__price product-suggestion-list__price-skeleton' />
                                <ColorPalettePreview colors={undefined} isLoading />
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }
    if (!isLoading && !products?.length) {
        return <div className='product-suggestion-list product-suggestion-list--empty'>{emptyText}</div>;
    }
    return (
        <ul className='product-suggestion-list'>
            {products?.map(product => (
                <li key={product.relewiseVariantId} className='product-suggestion-list__item-wrapper'>
                    <a href={product.productPageUrl} className='product-suggestion-list__item'>
                        <ImageWithLoading
                            wrapperClassname='product-suggestion-list__image-wrapper'
                            src={product.imgUrl}
                            alt={product.displayName}
                            title={product.displayName}
                            imageSettings={{
                                viewports: imageSettings
                            }}
                            gridSettings={gridSettings ?? {}}
                            loadFailureBehavior='empty'
                            showEmptyImageThumbnail
                            location={SEARCH_MODULE_NAME}
                            telemetry={telemetry}
                            variantConcatenatedId={product.concatenatedId}
                        />
                        <ProductLabels labels={product.labels} maxProductLabels={maxProductLabels} />
                        <div className='product-suggestion-list__text-container'>
                            <span className='product-suggestion-list__title'>{product.displayName}</span>
                            <div className='product-suggestion-list__price'>
                                {!!product.memberPriceData ? (
                                    <>
                                        <MemberPriceTag
                                            memberPrice={product.memberPriceData}
                                            className='product-suggestion-list__final-price'
                                        />
                                        <span className={'product-suggestion-list__final-price'}>{product.basePriceFormatted}</span>
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className={classnames('product-suggestion-list__final-price', {
                                                'product-suggestion-list__final-price--discounted': product.hasDiscount
                                            })}
                                        >
                                            {product.finalPriceFormatted}
                                        </span>
                                        {product.hasDiscount && (
                                            <span className='product-suggestion-list__original-price'>{product.basePriceFormatted}</span>
                                        )}
                                    </>
                                )}
                            </div>
                            {product.productColors && product.productColors.length > 0 && (
                                <ColorPalettePreview colors={product.productColors} />
                            )}
                        </div>
                    </a>
                </li>
            ))}
        </ul>
    );
};

interface SeeMoreButtonProps {
    searchUrl: string;
    ariaLabel: string;
    buttonText: string;
    isLoading: boolean;
}
const SeeMoreButton = ({ searchUrl, ariaLabel, buttonText, isLoading }: SeeMoreButtonProps) => {
    if (isLoading) {
        return <Skeleton className='new-search__see-more-btn' />;
    }
    return (
        <a href={searchUrl} aria-label={ariaLabel} className='new-search__see-more-btn'>
            {buttonText}
        </a>
    );
};

interface NewSearchEmptyStateProps {
    title: string;
    body: string;
}
const NewSearchEmptyState = ({ title, body }: NewSearchEmptyStateProps) => {
    return (
        <div className='new-search-empty-state'>
            <h2 className='new-search-empty-state__text'>{title}</h2>
            <p className='new-search-empty-state__body'>{body}</p>
        </div>
    );
};

const NewSearch = (props: ICusNewSearchProps<never>) => {
    const { config, resources, context } = props;
    const {
        enableCategorySuggestions,
        enableKeywordSuggestions,
        enableProductSuggestions,
        numberOfCategorySuggestions,
        numberOfKeywordSuggestions,
        numberOfProductSuggestions,
        searchPlaceholderText
    } = config;

    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);

    useEffect(function recoverSearchInputAfterHydration() {
        const initialSearchText = inputRef.current?.value;
        if (initialSearchText) {
            setSearchText(initialSearchText);
            setIsOpen(true);
        }
    }, []);

    const availableGenders = getAvailableGendersForLocale(context.request.app.config.localeGenderUrls, context.request.locale);
    const showGenderFilters = availableGenders.length >= MIN_GENDERS_FOR_SHOWING_FILTERS;
    const cookieGender = useGenderCookieValue(context.request);
    const [selectedGender, setSelectedGender] = useState<Gender>();
    const preferredGender = selectedGender ?? cookieGender ?? DEFAULT_GENDER;
    const gender = availableGenders.includes(preferredGender) ? preferredGender : availableGenders[0];

    useScrollLock(isOpen);
    const imageSettings = useImageSettings(IMAGE_VIEWPORT_SETTINGS);
    const isMobile = useIsMobile(context);
    const { debouncedValue: debouncedSearchText, isWaitingForFirstDebouncedValue } = useDebounce(searchText, DEBOUNCE_DELAY_MS);

    const { numberOfCategoryResults, numberOfKeywordResults, numberOfProductResults } = useMemo(
        () => ({
            numberOfCategoryResults: Math.min(
                numberOfCategorySuggestions ?? DEFAULT_NUMBER_OF_CATEGORY_SUGGESTIONS,
                MAX_NUMBER_OF_CATEGORY_SUGGESTIONS
            ),
            numberOfProductResults: Math.min(
                numberOfProductSuggestions ?? DEFAULT_NUMBER_OF_PRODUCT_SUGGESTIONS,
                MAX_NUMBER_OF_PRODUCT_SUGGESTIONS
            ),
            numberOfKeywordResults: Math.min(
                numberOfKeywordSuggestions ?? DEFAULT_NUMBER_OF_KEYWORD_SUGGESTIONS,
                MAX_NUMBER_OF_KEYWORD_SUGGESTIONS
            )
        }),
        [numberOfCategorySuggestions, numberOfKeywordSuggestions, numberOfProductSuggestions]
    );

    const { isLoading: areSuggestionsLoading, suggestions } = useSearchSuggestions({
        query: isOpen ? debouncedSearchText : '',
        gender,
        shouldFetchCategories: enableCategorySuggestions ?? false,
        shouldFetchProducts: enableProductSuggestions ?? false,
        shouldFetchSearchTermPredictions: enableKeywordSuggestions ?? false,
        numberOfCategoryResults,
        numberOfProductResults,
        numberOfKeywordResults,
        request: context.request,
        telemetry: context.telemetry
    });

    const openModal = () => {
        setIsOpen(true);
    };
    const closeModal = useCallback(() => {
        setIsOpen(false);
        setSearchText('');
        setSelectedGender(undefined);
    }, []);

    useEffect(
        function focusMobileSearchInput() {
            if (isOpen && isMobile) {
                inputRef.current?.focus({ preventScroll: true });
            }
        },
        [isOpen, isMobile]
    );

    useEffect(
        function closeModalIfAnyMicrosoftModalsAreOpen() {
            if (!isOpen) {
                return;
            }

            const observer = new MutationObserver(mutations => {
                for (const m of mutations) {
                    const el = m.target as HTMLElement;

                    if (el.classList?.contains('msc-modal') && el.classList.contains('show')) {
                        closeModal();
                        return;
                    }
                }
            });

            observer.observe(document.body, {
                subtree: true,
                attributes: true,
                attributeFilter: ['class']
            });

            return () => observer.disconnect();
        },
        [isOpen, closeModal]
    );

    useEffect(
        function closeModalOnEscape() {
            if (!isOpen) {
                return;
            }

            const handleGlobalKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    closeModal();
                }
            };

            window.addEventListener('keydown', handleGlobalKeyDown);
            return () => {
                window.removeEventListener('keydown', handleGlobalKeyDown);
            };
        },
        [isOpen, closeModal]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        if (!isMobile) {
            if (value.length) {
                openModal();
            } else {
                closeModal();
            }
        }
    };

    const getSearchUrl = (searchTerm = searchText) => {
        const sitePath = context.request.sitePath;
        const params = new URLSearchParams({ q: searchTerm });
        if (showGenderFilters && gender) {
            params.set('gender', gender);
        }
        return `${sitePath}/search?${params.toString()}`;
    };

    const navigateToSearch = () => {
        window.location.href = getSearchUrl();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !!searchText.length) {
            navigateToSearch();
        }
    };

    const handleGenderKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!gender || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = (availableGenders.indexOf(gender) + direction + availableGenders.length) % availableGenders.length;
        event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus();
        setSelectedGender(availableGenders[nextIndex]);
    };

    const handleSearchIconClick = () => {
        if (isMobile) {
            openModal();
        }
        if (!isMobile) {
            inputRef.current?.focus();
            if (searchText) {
                navigateToSearch();
            }
        }
    };

    const sharedInputProps: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> = {
        className: 'new-search__input',
        name: 'header-search',
        type: 'search',
        value: searchText,
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        placeholder: searchPlaceholderText ?? resources.searchtext,
        maxLength: context?.app?.config?.searchInputMaxLength ?? FALLBACK_MAX_SEARCH_CHARS,
        'aria-label': resources.searchLabelArialLabel,
        'aria-expanded': isOpen,
        autoComplete: 'off',
        ref: inputRef
    };

    const showEmptyState = isMobile && !debouncedSearchText;
    const isLoading = areSuggestionsLoading || isWaitingForFirstDebouncedValue;

    return (
        <div className={classnames('new-search', { 'new-search--mobile': isMobile, 'new-search--loading': isLoading })}>
            <div className='new-search__input-wrapper'>
                {!isMobile && <input {...sharedInputProps} data-testid='search-input' />}
                <button
                    className='new-search__icon-btn'
                    data-testid='search-icon-btn'
                    aria-label={resources.searchBtnAriaLabel}
                    aria-expanded={isOpen}
                    onClick={handleSearchIconClick}
                    type='button'
                />
            </div>

            <div
                className={classnames('new-search__modal', { 'new-search__modal--open': isOpen })}
                aria-hidden={!isOpen}
                {...{ inert: isOpen ? undefined : '' }}
            >
                <div className='new-search__modal-header'>
                    <button
                        className='new-search__close-btn'
                        aria-label={resources.cancelBtnAriaLabel}
                        onClick={closeModal}
                        type='button'
                    />
                    {isMobile && <input {...sharedInputProps} data-testid='search-input' />}
                    {showGenderFilters && (
                        <div
                            className='new-search__gender'
                            role='radiogroup'
                            aria-label={resources.searchGenderAriaLabel}
                            onKeyDown={handleGenderKeyDown}
                        >
                            {availableGenders.map(option => (
                                <Button
                                    className='new-search__gender-option'
                                    key={option}
                                    variant={gender === option ? 'primary' : 'secondary'}
                                    role='radio'
                                    aria-checked={gender === option}
                                    tabIndex={gender === option ? 0 : -1}
                                    onClick={() => setSelectedGender(option)}
                                    data-testid={`search-gender-${option}`}
                                >
                                    {option === 'female' ? resources.searchGenderFemaleText : resources.searchGenderMaleText}
                                </Button>
                            ))}
                        </div>
                    )}
                    {!showEmptyState && (
                        <h2 className='new-search__result-heading'>{format(resources.searchSuggestionHeading, `"${searchText}"`)}</h2>
                    )}
                </div>

                <div className='new-search__modal-body'>
                    {showEmptyState ? (
                        <NewSearchEmptyState title={resources.emptyMobileSearchTextHeading} body={resources.emptyMobileSearchText} />
                    ) : (
                        <>
                            {enableKeywordSuggestions && (
                                <>
                                    <h3 className='new-search__result-subheading'>{resources.keywordsHeading}</h3>
                                    <SimpleSuggestionList
                                        items={suggestions?.keywords?.map(keyword => ({
                                            ...keyword,
                                            url: getSearchUrl(keyword.label)
                                        }))}
                                        isLoading={isLoading}
                                        numberOfSuggestions={numberOfKeywordResults}
                                        emptyText={resources.noResultText}
                                    />
                                </>
                            )}
                            {enableCategorySuggestions && (
                                <>
                                    <h3 className='new-search__result-subheading'>{resources.categorySuggestionHeading}</h3>
                                    <SimpleSuggestionList
                                        items={suggestions?.categories}
                                        isLoading={isLoading}
                                        numberOfSuggestions={numberOfCategoryResults}
                                        emptyText={resources.noResultText}
                                    />
                                </>
                            )}
                            {enableProductSuggestions && (
                                <>
                                    <h3 className='new-search__result-subheading'>{resources.productSuggestionHeading}</h3>
                                    <ProductSuggestionList
                                        products={suggestions?.products}
                                        imageSettings={imageSettings}
                                        gridSettings={context.request.gridSettings}
                                        isLoading={isLoading}
                                        numberOfSuggestions={numberOfProductResults}
                                        emptyText={resources.noResultText}
                                        maxProductLabels={context.request.app?.config.maxProductLabels}
                                        telemetry={context.telemetry}
                                    />
                                    {!!suggestions?.products?.length && (
                                        <SeeMoreButton
                                            searchUrl={getSearchUrl()}
                                            ariaLabel={resources.seeMoreBtnAriaLabel}
                                            buttonText={resources.seeMoreButtonText}
                                            isLoading={isLoading}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewSearch;
